import {WriteButton} from '../../../common/buttons/WriteActions';
import {Box, Button, Skeleton, Stack} from '@mui/material';
import {ArrowBackIosNew} from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {recipeAPI} from '../../../../services/RecipeServices';
import {getAllItemsV2, getAllProductTypesForRecipes} from '../../dataManager';
import {EditRecipe} from './EditRecipe';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {IRecipe} from '../../../../models/Recipe/IRecipe';
import {useMemo, useState} from 'react';
import {DTO_STATE} from '@mybake/ui/common/dictionary';
import {DeleteRecipeDialog} from '../DeleteRecipeDialog';
import {getApiErrorMessage} from '../../../../common/apiErrors';
import {recipeDetailsComparisonSnapshot, recipeGuidForForm, recipeGuidRequest, recipePartComparisonSnapshot, recipePartGuidRequest} from '../../shared/recipeFormHelpers';
import {fillingAPI} from '../../../../services/FillingServices';

export const EditRecipeHOC = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const {t} = useAppTranslation();
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const {
        data: recipeData,
        isLoading: recipeLoading,
        isSuccess: recipeSuccess
    } = recipeAPI.useGetRecipeV2Query(id ?? '', {skip: !id});
    const recipeFormData = useMemo(() => recipeData ? recipeGuidForForm(recipeData) : undefined, [recipeData]);
    const {items, getAllItemsIsLoading, getAllItemsIsSuccess} = getAllItemsV2();
    const {productTypes, isLoadingProductTypes, isSuccessProductTypes} = getAllProductTypesForRecipes();
    const {data: fillings, isLoading: fillingsLoading, isSuccess: fillingsSuccess} = fillingAPI.useGetFillingsV2AllLightQuery();
    const [saveRecipe] = recipeAPI.useUpdateRecipeV2Mutation();
    const [createRecipePart] = recipeAPI.useCreateRecipePartV2Mutation();
    const [updateRecipePart] = recipeAPI.useUpdateRecipePartV2Mutation();
    const [attachRecipePart] = recipeAPI.useAttachRecipePartV2Mutation();
    const [detachRecipePart] = recipeAPI.useDetachRecipePartV2Mutation();
    const [deleteRecipe] = recipeAPI.useDeleteRecipeV2Mutation();
    const ready = recipeSuccess && !!recipeData && !!recipeFormData && getAllItemsIsSuccess && isSuccessProductTypes && fillingsSuccess;
    const loading = recipeLoading || getAllItemsIsLoading || isLoadingProductTypes || fillingsLoading;
    const handleSubmit = async (value: IRecipe) => {
        const editedId = recipeData?.id ?? String(value.id);
        const returnPage = (location.state as { returnPage?: number } | null)?.returnPage;
        const currentParts = value.parts ?? [];
        const recipeDetailsChanged = JSON.stringify(recipeDetailsComparisonSnapshot(value)) !== JSON.stringify(recipeDetailsComparisonSnapshot(recipeFormData));
        if (recipeDetailsChanged) {
            await saveRecipe({id: editedId, body: recipeGuidRequest(value, true)}).unwrap();
        }
        for (let sortOrder = 0; sortOrder < currentParts.length; sortOrder += 1) {
            const part = currentParts[sortOrder];
            if (part.clientOrigin === 'library') {
                if (part.state !== DTO_STATE.DTO_DELETED) {
                    const partChanged = JSON.stringify(recipePartComparisonSnapshot(part)) !== part.clientOriginalSnapshot;
                    if (partChanged) await updateRecipePart({id: String(part.id), body: recipePartGuidRequest(part)}).unwrap();
                    await attachRecipePart({recipeId: editedId, partId: String(part.id), isMainPart: Boolean(part.isMainPart), sortOrder}).unwrap();
                }
            } else if (part.state === DTO_STATE.DTO_NEW) {
                const createdPart = await createRecipePart(recipePartGuidRequest(part)).unwrap();
                await attachRecipePart({recipeId: editedId, partId: createdPart.id, isMainPart: Boolean(part.isMainPart), sortOrder}).unwrap();
            } else if (part.state === DTO_STATE.DTO_MODIFIED) {
                const originalPart = recipeFormData?.parts?.find(original => original.id === part.id);
                const partChanged = JSON.stringify(recipePartComparisonSnapshot(part)) !== JSON.stringify(recipePartComparisonSnapshot(originalPart));
                if (partChanged) await updateRecipePart({id: String(part.id), body: recipePartGuidRequest(part)}).unwrap();
            } else if (part.state === DTO_STATE.DTO_DELETED) {
                await detachRecipePart({recipeId: editedId, partId: String(part.id)}).unwrap();
            }
        }
        navigate('/app/recipes', {state: {expandedId: editedId, page: returnPage}});
    };
    const handleDelete = async () => {
        if (!recipeData) return;
        setDeleteError('');
        try {
            await deleteRecipe(recipeData.id).unwrap();
            navigate('/app/recipes');
        } catch (error) {
            setDeleteError(getApiErrorMessage(error, t));
        }
    };
    return <Box sx={{width: '100%'}}>
        <Button variant="outlined" startIcon={<ArrowBackIosNew/>}
                onClick={() => navigate('/app/recipes')}>{t('recipes:back_to_recipes')}</Button>
        {loading &&
            <Stack spacing={2} sx={{mt: 2}}><Skeleton variant="rounded" height={120}/><Skeleton variant="rounded"
                                                                                                height={220}/><Skeleton
                variant="rounded" height={220}/></Stack>}
        {ready && recipeFormData && <><EditRecipe recipeData={recipeFormData} productTypes={productTypes} items={items} fillings={fillings}
                              onSubmit={handleSubmit}/><WriteButton sx={{mt: 2}} color="error" variant="outlined" startIcon={<DeleteIcon/>} onClick={() => setDeleteOpen(true)}>{t('recipes:delete_recipe')}</WriteButton>
            <DeleteRecipeDialog open={deleteOpen} onClose={() => setDeleteOpen(false)} recipeName={recipeData.recipeName ?? ''} onDelete={handleDelete} error={deleteError}/></>}
    </Box>;
};
